import { useState, useEffect } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faEnvelope,
  faSms,
  faSave,
  faCheckCircle,
  faExclamationCircle,
  faClock,
  faChartBar,
  faUserPlus,
  faFileAlt,
  faCalendarDay,
  faCalendarWeek,
  faToggleOn,
  faToggleOff,
  faCog,
} from "@fortawesome/free-solid-svg-icons";
import { toastError, toastSuccess } from "../../utils/toast";

const ManageNotificationO = () => {
  const [settings, setSettings] = useState({
    methods: {
      email: true,
      sms: false,
    },
    types: {
      newSale: {
        enabled: true,
        email: true,
        sms: true,
        description: "When a seller enters a new sale record",
      },
      salesReport: {
        enabled: true,
        email: true,
        sms: true,
        frequency: "daily",
        description: "Sales report notifications",
      },
      stockAlert: {
        enabled: true,
        email: true,
        sms: false,
        description: "When stock levels are low",
      },
      newCustomer: {
        enabled: false,
        email: true,
        sms: false,
        description: "When a new customer is registered",
      },
      paymentReceived: {
        enabled: true,
        email: true,
        sms: true,
        description: "When a payment is received",
      },
      systemUpdate: {
        enabled: true,
        email: true,
        sms: false,
        description: "System updates and maintenance",
      },
    },
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    document.title = "Manage Notifications - Shop Kiganjani";
  }, []);

  // Toggle notification method (Email/SMS)
  const toggleMethod = (method) => {
    setSettings((prev) => ({
      ...prev,
      methods: {
        ...prev.methods,
        [method]: !prev.methods[method],
      },
    }));
  };

  // Toggle notification type enabled/disabled
  const toggleTypeEnabled = (typeKey) => {
    setSettings((prev) => ({
      ...prev,
      types: {
        ...prev.types,
        [typeKey]: {
          ...prev.types[typeKey],
          enabled: !prev.types[typeKey].enabled,
        },
      },
    }));
  };

  // Toggle notification method for specific type
  const toggleTypeMethod = (typeKey, method) => {
    setSettings((prev) => ({
      ...prev,
      types: {
        ...prev.types,
        [typeKey]: {
          ...prev.types[typeKey],
          [method]: !prev.types[typeKey][method],
        },
      },
    }));
  };

  // Update frequency for sales report
  const updateFrequency = (value) => {
    setSettings((prev) => ({
      ...prev,
      types: {
        ...prev.types,
        salesReport: {
          ...prev.types.salesReport,
          frequency: value,
        },
      },
    }));
  };

  // Handle save settings
  const handleSave = () => {
    setIsSaving(true);

    const hasMethod = settings.methods.email || settings.methods.sms;
    if (!hasMethod) {
      toastError(
        "Please select at least one notification method (Email or SMS)",
      );
      setIsSaving(false);
      return;
    }

    const hasEnabledType = Object.values(settings.types).some(
      (type) => type.enabled,
    );
    if (!hasEnabledType) {
      toastError("Please enable at least one notification type");
      setIsSaving(false);
      return;
    }

    setTimeout(() => {
      setIsSaving(false);
      toastSuccess("Notification settings saved successfully!");
    }, 1000);
  };

  // Get frequency label
  const getFrequencyLabel = (freq) => {
    const labels = {
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      yearly: "Yearly",
    };
    return labels[freq] || freq;
  };

  // Get frequency icon
  const getFrequencyIcon = (freq) => {
    const icons = {
      daily: faCalendarDay,
      weekly: faCalendarWeek,
      monthly: faCalendarWeek,
      yearly: faCalendarDay,
    };
    return icons[freq] || faClock;
  };

  // Notification type icons
  const typeIcons = {
    newSale: faUserPlus,
    salesReport: faChartBar,
    stockAlert: faExclamationCircle,
    newCustomer: faUserPlus,
    paymentReceived: faFileAlt,
    systemUpdate: faBell,
  };

  // Count active notifications
  const getActiveCount = () => {
    return Object.values(settings.types).filter((type) => type.enabled).length;
  };

  const getMethodCount = () => {
    let count = 0;
    if (settings.methods.email) count++;
    if (settings.methods.sms) count++;
    return count;
  };

  // Frequency options for responsive display
  const frequencyOptions = [
    { value: "daily", label: "Daily", icon: faCalendarDay },
    { value: "weekly", label: "Weekly", icon: faCalendarWeek },
    { value: "monthly", label: "Monthly", icon: faCalendarWeek },
    { value: "yearly", label: "Yearly", icon: faCalendarDay },
  ];

  return (
    <div className="flex flex-col gap-6 max-w-5xl mx-auto px-4 sm:px-0">
      {/* Header */}
      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faBell}
                className="text-blue-800 h-4 w-4 sm:h-5 sm:w-5"
              />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
                Manage notifications
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                Configure how and when you receive notifications
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Badge
              variant="outline"
              className="bg-green-50 text-green-700 border-green-200 px-2 sm:px-3 py-1 text-xs"
            >
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                {getActiveCount()} Active
              </span>
            </Badge>
            <Badge
              variant="outline"
              className="bg-blue-50 text-blue-700 border-blue-200 px-2 sm:px-3 py-1 text-xs"
            >
              {getMethodCount()} Method{getMethodCount() !== 1 ? "s" : ""}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Notification Methods */}
      <Card className="bg-white border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={faCog} className="text-blue-800 h-4 w-4" />
            Notification methods
          </h3>
          <p className="text-xs text-gray-500">
            Select how you want to receive notifications
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {/* Email Method */}
          <div
            className={`flex items-center justify-between p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
              settings.methods.email
                ? "border-blue-600 bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            }`}
            onClick={() => toggleMethod("email")}
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${
                  settings.methods.email ? "bg-blue-600" : "bg-gray-200"
                }`}
              >
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${settings.methods.email ? "text-white" : "text-gray-500"}`}
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Email</p>
                <p className="text-xs text-gray-500 truncate">
                  Receive via email
                </p>
              </div>
            </div>
            <div className="ml-2 shrink-0">
              <Badge
                variant="outline"
                className={
                  settings.methods.email
                    ? "bg-green-100 text-green-700 border-green-200 text-xs"
                    : "bg-gray-100 text-gray-500 border-gray-200 text-xs"
                }
              >
                {settings.methods.email ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          </div>

          {/* SMS Method */}
          <div
            className={`flex items-center justify-between p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
              settings.methods.sms
                ? "border-blue-600 bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            }`}
            onClick={() => toggleMethod("sms")}
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${
                  settings.methods.sms ? "bg-blue-600" : "bg-gray-200"
                }`}
              >
                <FontAwesomeIcon
                  icon={faSms}
                  className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${settings.methods.sms ? "text-white" : "text-gray-500"}`}
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">SMS</p>
                <p className="text-xs text-gray-500 truncate">
                  Receive via text
                </p>
              </div>
            </div>
            <div className="ml-2 shrink-0">
              <Badge
                variant="outline"
                className={
                  settings.methods.sms
                    ? "bg-green-100 text-green-700 border-green-200 text-xs"
                    : "bg-gray-100 text-gray-500 border-gray-200 text-xs"
                }
              >
                {settings.methods.sms ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* Notification Types */}
      <Card className="bg-white border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={faBell} className="text-blue-800 h-4 w-4" />
            Notification types
          </h3>
          <p className="text-xs text-gray-500">
            Configure what you want to be notified about
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {Object.entries(settings.types).map(([key, type]) => {
            const Icon = typeIcons[key] || faBell;
            const isSalesReport = key === "salesReport";

            return (
              <div
                key={key}
                className={`rounded-lg border-2 transition-all duration-200 ${
                  type.enabled
                    ? "border-blue-200 bg-white"
                    : "border-gray-200 bg-gray-50 opacity-70"
                }`}
              >
                <div className="p-3 sm:p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${
                          type.enabled ? "bg-blue-100" : "bg-gray-200"
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={Icon}
                          className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${type.enabled ? "text-blue-600" : "text-gray-400"}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                          <p className="text-sm font-medium text-gray-900">
                            {key.replace(/([A-Z])/g, " $1").trim()}
                          </p>
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${
                              type.enabled
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-500 border-gray-200"
                            }`}
                          >
                            {type.enabled ? "Active" : "Inactive"}
                          </Badge>
                          {isSalesReport && type.enabled && (
                            <Badge
                              variant="outline"
                              className="bg-purple-100 text-purple-700 border-purple-200 text-[10px]"
                            >
                              <FontAwesomeIcon
                                icon={getFrequencyIcon(type.frequency)}
                                className="mr-1 h-2 w-2"
                              />
                              {getFrequencyLabel(type.frequency)}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {type.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 ml-11 sm:ml-0">
                      {/* Enable/Disable Switch */}
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={type.enabled ? faToggleOn : faToggleOff}
                          className={`h-5 w-5 sm:h-6 sm:w-6 cursor-pointer transition-colors ${
                            type.enabled ? "text-blue-600" : "text-gray-400"
                          }`}
                          onClick={() => toggleTypeEnabled(key)}
                        />
                      </div>

                      {/* Method Toggles */}
                      {type.enabled && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleTypeMethod(key, "email")}
                            className={`flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-md text-[10px] sm:text-xs transition-all ${
                              type.email
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-400"
                            }`}
                            disabled={!settings.methods.email}
                          >
                            <FontAwesomeIcon
                              icon={faEnvelope}
                              className="h-2.5 w-2.5 sm:h-3 sm:w-3"
                            />
                            <span className="hidden xs:inline">Email</span>
                          </button>

                          <button
                            onClick={() => toggleTypeMethod(key, "sms")}
                            className={`flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-md text-[10px] sm:text-xs transition-all ${
                              type.sms
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-400"
                            }`}
                            disabled={!settings.methods.sms}
                          >
                            <FontAwesomeIcon
                              icon={faSms}
                              className="h-2.5 w-2.5 sm:h-3 sm:w-3"
                            />
                            <span className="hidden xs:inline">SMS</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Frequency Selector - Responsive */}
                  {isSalesReport && type.enabled && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        <Label className="text-xs text-gray-600 font-medium flex items-center gap-1.5 shrink-0">
                          <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
                          Report Frequency:
                        </Label>
                        <div className="flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
                          {frequencyOptions.map((freq) => (
                            <button
                              key={freq.value}
                              onClick={() => updateFrequency(freq.value)}
                              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md text-[10px] sm:text-xs font-medium transition-all duration-200 flex items-center justify-center gap-1 sm:gap-1.5 ${
                                type.frequency === freq.value
                                  ? "bg-blue-800 text-white shadow-sm"
                                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
                              }`}
                            >
                              <FontAwesomeIcon
                                icon={freq.icon}
                                className="h-2.5 w-2.5 sm:h-3 sm:w-3"
                              />
                              <span>{freq.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Save Button */}
      <Card className="bg-white border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-green-600 h-3.5 w-3.5 sm:h-4 sm:w-4"
              />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Ready to save</p>
              <p className="text-xs text-gray-500 truncate">
                {getActiveCount()} notification types active with{" "}
                {getMethodCount()} method{getMethodCount() !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white px-6 sm:px-8 py-2 sm:py-2.5 w-full sm:w-auto min-w-[120px] sm:min-w-[150px]"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-white border-t-transparent" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon={faSave}
                  className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                />
                <span>Save settings</span>
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ManageNotificationO;
